import React, { useState, useEffect, Suspense } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Grid,
  Paper,
  Button,
  Card,
} from "@mui/material";
import Close from "@mui/icons-material/Close";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import SuspenseLoading from "./SuspenseLoading";
import englishAudio from "../assets/audios/English.mp3"; //
import arabicAudio from "../assets/audios/Arabic.mp3"; //
import spanishAudio from "../assets/audios/spanish.wav"; //
import frenchAudio from "../assets/audios/french.wav"; //
import germanAudio from "../assets/audios/german.wav"; //

import { connect } from "react-redux";
import { updatePostFormData as updatePostFormDataAction } from "../redux/slices/postsSlice";
import { enqueueSnackbar } from "notistack";

// Language data with flags, names, and audio samples (URLs would be replaced with actual audio files)
const languageData = [
  {
    id: "en",
    name: "English",
    flag: "🇺🇸",
    audio: englishAudio,
    available: true,
  },
  {
    id: "ar",
    name: "Arabic",
    flag: "🇸🇦",
    audio: arabicAudio,
    available: true,
  },
  {
    id: "de",
    name: "German",
    flag: "🇩🇪",
    audio: germanAudio,
    available: true,
  },
  {
    id: "fr",
    name: "French",
    flag: "🇫🇷",
    audio: frenchAudio,
    available: true,
  },
  {
    id: "es",
    name: "Spanish",
    flag: "🇪🇸",
    audio: spanishAudio,
    available: true,
  },
  {
    id: "hi",
    name: "Hindi",
    flag: "🇮🇳",
    audio: "/path/to/hindi-sample.mp3",
    available: false,
  },
  {
    id: "ur",
    name: "Urdu",
    flag: "🇵🇰",
    audio: "/path/to/urdu-sample.mp3",
    available: false,
  },
];

// CSS for the shimmer effect
const shimmerStyle = `
@keyframes shimmer {
  0% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.5;
  }
}

.shimmer-text {
  animation: shimmer 1.5s infinite;
  color: #9048FB;
}
`;

const SelectLanguage = ({
  isModal,
  onNext,
  updatePostFormData,
  triggerValidation,
  setLangaugesForInterview,
  onSelect,
  modal2,
  onClose,
}) => {
  const [selectedLanguages, setSelectedLanguages] = useState(["en"]);
  const [audioPlaying, setAudioPlaying] = useState(null);
  const [speaking, setSpeaking] = useState(false);

  // For handling audio playback
  const audioRef = React.useRef(new Audio());

  useEffect(() => {
    // Cleanup audio on unmount
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  useEffect(() => {
    if (triggerValidation) {
      onNext();
      updatePostFormData({ languages: selectedLanguages });
    } else if (isModal) {
      setLangaugesForInterview(selectedLanguages);
    } else if (modal2) {
      onSelect(selectedLanguages);
    }
  }, [triggerValidation, isModal]);

  const handleToggleLanguage = async (languageId) => {
    let updatedLanguages = [...selectedLanguages];

    if (selectedLanguages.includes(languageId)) {
      // Don't remove if it's the last selected language
      if (selectedLanguages.length > 1) {
        updatedLanguages = selectedLanguages.filter((id) => id !== languageId);
        setSelectedLanguages(updatedLanguages);
        if (isModal) {
          setLangaugesForInterview(updatedLanguages);
        } else if (modal2) {
          onSelect(updatedLanguages);
        }
      }
    } else {
      // Don't add if already at max (4)
      if (selectedLanguages.length < 4) {
        updatedLanguages = [...selectedLanguages, languageId];
        setSelectedLanguages(updatedLanguages);

        if (isModal) {
          setLangaugesForInterview(updatedLanguages);
        } else if (modal2) {
          onSelect(updatedLanguages);
        }
      } else {
        enqueueSnackbar(`Max 4 languages can be selected for now.`, {
          variant: "info",
        });
      }
    }
  };

  const playAudio = (languageId) => {
    // Stop any currently playing audio
    audioRef.current.pause();

    // Find the language data
    const language = languageData.find((lang) => lang.id === languageId);
    if (language) {
      setSpeaking(true);
      audioRef.current = new Audio(language.audio);
      audioRef.current.play();
      setAudioPlaying(languageId);

      // Reset playing state when audio ends
      audioRef.current.onended = () => {
        setAudioPlaying(null);
        setSpeaking(false);
      };
    }
  };

  return (
    <Card
      sx={{ m: isModal ? 0 : 4, p: 1.5, height: "auto", borderRadius: "10px" }}
    >
      <style>{shimmerStyle}</style>
      <Box
        sx={{
          py: 1.5,
          px: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Typography
          color="black"
          fontFamily="Outfit"
          fontWeight={600}
          fontSize={{ xs: "14px", md: "18px" }}
        >
          Select languages for assessment
        </Typography>

        {modal2 && (
          <IconButton
            size="small"
            sx={{
              bgcolor: "white",
              ":hover": { bgcolor: "white" },
              border: "1px solid",
            }}
            onClick={onClose}
          >
            <Close />
          </IconButton>
        )}
      </Box>
      <Suspense fallback={<SuspenseLoading width="100%" height="80%" />}>
        <Box
          sx={{
            py: 1.5,
            px: 2,
          }}
        >
          <Typography
            color="#4E4E4E"
            sx={{ mb: 3, fontFamily: "Outfit", fontWeight: 400 }}
          >
            Select the languages candidates can choose from before the interview
            starts. If you select multiple languages, candidates will have the
            option to pick one. If you select only one language, the interview
            will proceed in that language without an option to change it.
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Typography variant="subtitle2">Available</Typography>
              <Typography variant="subtitle2" color="#762FDE">
                (Multi selection available)
              </Typography>
            </Box>

            <Grid container spacing={2}>
              {languageData
                ?.filter((lang) => lang.available)
                ?.map((language) => (
                  <Grid item xs={12} sm={6} key={language.id}>
                    <Paper
                      elevation={0}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 1,
                        borderRadius: 8,
                        border: "3px solid",
                        borderColor: selectedLanguages.includes(language.id)
                          ? "success.main"
                          : "divider",
                        bgcolor: "background.paper",
                        "&:hover": {
                          borderColor: selectedLanguages.includes(language.id)
                            ? "success.main"
                            : "divider",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          flexGrow: 1,
                        }}
                      >
                        {/* Flag */}
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            overflow: "hidden",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mr: 2,
                          }}
                        >
                          <Typography sx={{ fontSize: "25px" }}>
                            {language?.flag}
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            fontFamily: "Outfit",
                            fontWeight: 400,
                            color: "#17171F",
                          }}
                        >
                          {language.name}
                        </Typography>

                        {/* Speaking Status */}
                        {audioPlaying === language.id && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              ml: 1,
                            }}
                          >
                            <Typography
                              className="shimmer-text"
                              sx={{
                                fontFamily: "Outfit",
                                fontSize: 12,
                              }}
                            >
                              Speaking...
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Tooltip title="Play sample">
                          <IconButton
                            onClick={() => playAudio(language.id)}
                            size="small"
                            sx={{
                              color: "#9048FB",
                            }}
                          >
                            {audioPlaying === language.id ? (
                              <PauseCircleOutlineIcon />
                            ) : (
                              <PlayCircleOutlineIcon />
                            )}
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          color={
                            selectedLanguages.includes(language.id)
                              ? "success"
                              : "default"
                          }
                          onClick={() => handleToggleLanguage(language.id)}
                          size="small"
                        >
                          {selectedLanguages.includes(language.id) ? (
                            <CheckCircleIcon />
                          ) : (
                            <RadioButtonUncheckedIcon />
                          )}
                        </IconButton>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
            </Grid>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Upcoming
            </Typography>
            <Grid container spacing={2}>
              {languageData
                .filter((lang) => !lang.available)
                .map((language) => (
                  <Grid item xs={12} sm={6} key={language.id}>
                    <Paper
                      elevation={0}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        p: 1,
                        borderRadius: 8,
                        border: "3px solid",
                        borderColor: "divider",
                        opacity: 0.7,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          flexGrow: 1,
                        }}
                      >
                        {/* Flag */}
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            overflow: "hidden",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mr: 2,
                          }}
                        >
                          <Typography sx={{ fontSize: "25px" }}>
                            {language?.flag}
                          </Typography>
                        </Box>
                        <Typography variant="body1">{language.name}</Typography>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
            </Grid>
          </Box>
        </Box>
      </Suspense>
    </Card>
  );
};

const mapDipatchToProps = {
  updatePostFormData: updatePostFormDataAction,
};

/* Export
============================================================================= */
export default connect(null, mapDipatchToProps)(SelectLanguage);
